import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Clock, ArrowUp, ArrowDown, AlertCircle, Moon } from 'lucide-react';
import { BathroomVisit } from '../BathroomSafety';

// Mock data for bathroom visits
const generateMockBathroomVisits = (): BathroomVisit[] => {
  const visits: BathroomVisit[] = [];
  const now = new Date();
  
  // Generate data for the past 7 days
  for (let i = 0; i < 7; i++) {
    const day = new Date();
    day.setDate(now.getDate() - i);
    
    // Generate 3-5 visits per day
    const visitsPerDay = 3 + Math.floor(Math.random() * 3);
    for (let j = 0; j < visitsPerDay; j++) {
      // Distribute throughout the day
      const hour = Math.floor(Math.random() * 24);
      const minute = Math.floor(Math.random() * 60);
      
      const timestamp = new Date(day);
      timestamp.setHours(hour, minute);
      
      // Nighttime is considered between 10PM and 6AM
      const isNighttime = hour >= 22 || hour < 6;
      
      // Duration between 1 and 10 minutes (in seconds)
      const duration = (1 + Math.floor(Math.random() * 9)) * 60;
      
      // Randomize other properties
      const movementPatterns: Array<'normal' | 'unstable' | 'rapid' | 'slow'> = ['normal', 'unstable', 'rapid', 'slow'];
      const pressureDistributions: Array<'balanced' | 'unbalanced' | 'shifting'> = ['balanced', 'unbalanced', 'shifting'];
      const exitStatuses: Array<'normal' | 'assisted' | 'emergency'> = ['normal', 'assisted', 'emergency'];
      
      // Higher chance of issues during nighttime
      const problemProbability = isNighttime ? 0.3 : 0.1;
      
      visits.push({
        id: `visit-${i}-${j}`,
        timestamp,
        duration,
        movementPattern: Math.random() < problemProbability ? 
                         movementPatterns[1 + Math.floor(Math.random() * 3)] : 
                         'normal',
        pressureDistribution: Math.random() < problemProbability ? 
                              pressureDistributions[1 + Math.floor(Math.random() * 2)] : 
                              'balanced',
        waterDetected: Math.random() < 0.2,
        exitStatus: Math.random() < problemProbability ? 
                    exitStatuses[1 + Math.floor(Math.random() * 2)] : 
                    'normal',
        isNighttime
      });
    }
  }
  
  // Sort by timestamp
  return visits.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

const mockBathroomVisits = generateMockBathroomVisits();

// Prepare data for charts
const prepareVisitsByDayData = (visits: BathroomVisit[]) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const data = days.map(day => ({
    day,
    daytime: 0,
    nighttime: 0
  }));
  
  visits.forEach(visit => {
    const dayIndex = visit.timestamp.getDay();
    if (visit.isNighttime) {
      data[dayIndex].nighttime++;
    } else {
      data[dayIndex].daytime++;
    }
  });
  
  // Reorder to make today the last day
  const today = new Date().getDay();
  return [...data.slice(today + 1), ...data.slice(0, today + 1)];
};

const prepareVisitsByHourData = (visits: BathroomVisit[]) => {
  const data = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    visits: 0,
    anomalies: 0
  }));
  
  visits.forEach(visit => {
    const hour = visit.timestamp.getHours();
    data[hour].visits++;
    
    // Count anomalies (non-normal patterns or distributions)
    if (visit.movementPattern !== 'normal' || 
        visit.pressureDistribution !== 'balanced' || 
        visit.exitStatus !== 'normal') {
      data[hour].anomalies++;
    }
  });
  
  return data;
};

const calculateAverages = (visits: BathroomVisit[]) => {
  if (visits.length === 0) return { duration: 0, frequency: 0, nighttime: 0 };
  
  const totalDuration = visits.reduce((sum, visit) => sum + visit.duration, 0);
  const avgDuration = totalDuration / visits.length;
  
  // Calculate daily frequency
  const days = new Set(visits.map(visit => visit.timestamp.toDateString())).size;
  const avgFrequency = visits.length / days;
  
  // Calculate nighttime percentage
  const nighttimeVisits = visits.filter(visit => visit.isNighttime).length;
  const nighttimePercentage = (nighttimeVisits / visits.length) * 100;
  
  return {
    duration: Math.round(avgDuration / 60 * 10) / 10, // in minutes, rounded to 1 decimal
    frequency: Math.round(avgFrequency * 10) / 10, // rounded to 1 decimal
    nighttime: Math.round(nighttimePercentage)
  };
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

const BathroomVisitAnalytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'week' | 'month'>('week');
  
  // Filter visits based on time range
  const filteredVisits = mockBathroomVisits.filter(visit => {
    const now = new Date();
    const timeLimit = new Date();
    timeLimit.setDate(now.getDate() - (timeRange === 'week' ? 7 : 30));
    return visit.timestamp >= timeLimit;
  });
  
  const visitsByDayData = prepareVisitsByDayData(filteredVisits);
  const visitsByHourData = prepareVisitsByHourData(filteredVisits);
  const averages = calculateAverages(filteredVisits);
  
  // Get the most recent visits
  const recentVisits = filteredVisits.slice(0, 5);
  
  return (
    <div className="p-6">
      {/* Time Range Selector */}
      <div className="mb-6">
        <div className="flex space-x-2">
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'week' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('week')}
          >
            Past Week
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'month' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('month')}
          >
            Past Month
          </button>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-blue-900">Avg. Duration</h3>
              <p className="text-3xl font-bold text-blue-700">{averages.duration} min</p>
            </div>
            <Clock className="text-blue-500" />
          </div>
          <div className="mt-2 text-sm text-blue-600">
            {Math.random() > 0.5 ? (
              <span className="flex items-center">
                <ArrowUp className="w-4 h-4 mr-1" /> 
                0.5 min from last {timeRange}
              </span>
            ) : (
              <span className="flex items-center">
                <ArrowDown className="w-4 h-4 mr-1" /> 
                0.3 min from last {timeRange}
              </span>
            )}
          </div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-green-900">Daily Frequency</h3>
              <p className="text-3xl font-bold text-green-700">{averages.frequency} visits</p>
            </div>
            <Clock className="text-green-500" />
          </div>
          <div className="mt-2 text-sm text-green-600">
            {Math.random() > 0.5 ? (
              <span className="flex items-center">
                <ArrowUp className="w-4 h-4 mr-1" /> 
                0.2 visits from last {timeRange}
              </span>
            ) : (
              <span className="flex items-center">
                <ArrowDown className="w-4 h-4 mr-1" /> 
                0.3 visits from last {timeRange}
              </span>
            )}
          </div>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-purple-900">Nighttime Visits</h3>
              <p className="text-3xl font-bold text-purple-700">{averages.nighttime}%</p>
            </div>
            <Clock className="text-purple-500" />
          </div>
          <div className="mt-2 text-sm text-purple-600">
            {Math.random() > 0.5 ? (
              <span className="flex items-center">
                <ArrowUp className="w-4 h-4 mr-1" /> 
                5% from last {timeRange}
              </span>
            ) : (
              <span className="flex items-center">
                <ArrowDown className="w-4 h-4 mr-1" /> 
                3% from last {timeRange}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Visits by Day</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={visitsByDayData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="daytime" name="Daytime Visits" fill="#3B82F6" />
              <Bar dataKey="nighttime" name="Nighttime Visits" fill="#7C3AED" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Visits by Hour</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={visitsByHourData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="hour" 
                tickFormatter={(hour) => `${hour}:00`}
              />
              <YAxis />
              <Tooltip
                formatter={(value, name) => [value, name]}
                labelFormatter={(hour) => `${hour}:00`}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="visits" 
                name="Total Visits" 
                stroke="#3B82F6" 
                strokeWidth={2} 
              />
              <Line 
                type="monotone" 
                dataKey="anomalies" 
                name="Anomalies" 
                stroke="#EF4444" 
                strokeWidth={2} 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Recent Visits */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="text-lg font-medium mb-4">Recent Visits</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Movement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Water</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentVisits.map((visit) => (
                <tr key={visit.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {visit.isNighttime && <Moon className="w-4 h-4 mr-2 text-purple-500" />}
                      <span className="text-sm text-gray-900">
                        {visit.timestamp.toLocaleDateString()} {visit.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{formatDuration(visit.duration)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      visit.movementPattern === 'normal' 
                        ? 'bg-green-100 text-green-800' 
                        : visit.movementPattern === 'unstable'
                        ? 'bg-red-100 text-red-800'
                        : visit.movementPattern === 'rapid'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {visit.movementPattern}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      visit.waterDetected 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {visit.waterDetected ? 'Detected' : 'None'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      visit.exitStatus === 'normal' 
                        ? 'bg-green-100 text-green-800' 
                        : visit.exitStatus === 'assisted'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {visit.exitStatus === 'emergency' && <AlertCircle className="w-3 h-3 mr-1" />}
                      {visit.exitStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BathroomVisitAnalytics; 